<script setup>
/**
 * One state, said once: a dot, a sentence, what it means, and the one thing to do about it
 * (ease package P-5.1).
 *
 * The state is the SERVER's (`ai-sidecar/src/status/compute.ts` ranks it, hardest stop first).
 * This component never ranks and never guesses: a state it has no words for reads "We couldn't
 * check your assistant just now", never "answering".
 *
 * THE FIX. `turn_on` writes `{ aiEnabled: true }` and hands the saved config up. `request_*`
 * asks us (one request a day; a second press is answered the same way, "we've got it").
 * `go_live` is the page's to confirm: on Overview (`goLiveHere`) it is handed up to the
 * readiness path, anywhere else it links to Overview. `request_top_up` is the Request top-up
 * button (P-5.3), the same one the credit card carries; `topUpTold` starts it in "We've already
 * been told" when the page knows.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import TopUpRequestButton from './TopUpRequestButton.vue';
import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';
import {
  STATUS_FIXES,
  STATUS_STATES,
  statusCopyKey,
  toneOf,
} from '../helpers/status.js';
import { toIntlLocale } from '../helpers/intl.js';

const props = defineProps({
  /** `{ state, fix, resumesAt }`: the account's, or one channel's. `null` = could not read. */
  status: { type: Object, default: null },
  accountId: { type: Number, default: null },
  /** The channel this is about, so "Ask us to connect it" asks about THAT channel. */
  inboxId: { type: Number, default: null },
  size: {
    type: String,
    default: 'large',
    validator: value => ['large', 'small'].includes(value),
  },
  /** A better second line the page already has (Overview: when it last replied). */
  detail: { type: String, default: '' },
  /** True on the page that holds the readiness path, which confirms going live itself. */
  goLiveHere: { type: Boolean, default: false },
  /** P-5.3: the credit read already says we were told; the top-up fix starts disabled. */
  topUpTold: { type: Boolean, default: false },
  /**
   * P-1.6: a team member is looking. A fix that needs an administrator is said as a sentence
   * ("Ask your account owner to …") and nothing is pressable.
   */
  asksOwner: { type: Boolean, default: false },
});

const emit = defineEmits(['goLive', 'changed']);

const { t, locale } = useWasloI18n();

const state = computed(() =>
  STATUS_STATES.includes(props.status?.state) ? props.status.state : null
);
const fix = computed(() =>
  state.value !== null && STATUS_FIXES.includes(props.status?.fix)
    ? props.status.fix
    : 'none'
);

const title = computed(() =>
  state.value === null
    ? t('WASLO.STATUS.UNKNOWN')
    : t(`${statusCopyKey(state.value)}.TITLE`)
);
const body = computed(() => {
  if (state.value === null) return '';
  return props.detail || t(`${statusCopyKey(state.value)}.BODY`);
});

/** Only a day limit lifts by itself, and the server says when (the next UTC midnight). */
const resumesLine = computed(() => {
  const at = props.status?.resumesAt;
  if (state.value !== 'day_limit' || typeof at !== 'string') return '';
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  const when = new Intl.DateTimeFormat(toIntlLocale(locale.value), {
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
  return t('WASLO.STATUS.RESUMES', { when });
});

// `n-blue-*` is the accent (the branding overlay renders it emerald in both themes).
const DOTS = {
  on: 'bg-n-blue-9',
  practice: 'bg-n-amber-9',
  stopped: 'bg-n-ruby-9',
  off: 'bg-n-slate-8',
};
const dot = computed(() =>
  state.value === null ? DOTS.off : DOTS[toneOf(state.value)]
);

const FIX_COPY = {
  turn_on: 'TURN_ON',
  go_live: 'GO_LIVE',
  request_connect: 'REQUEST_CONNECT',
  request_limit_raise: 'REQUEST_LIMIT_RAISE',
};
const fixLabel = computed(() => {
  // P-1.6: a channel WE switched off is switched back on by us, and the button says so.
  if (fix.value === 'request_connect' && state.value === 'channel_off') {
    return t('WASLO.STATUS.FIX.REQUEST_SWITCH_ON');
  }
  return FIX_COPY[fix.value]
    ? t(`WASLO.STATUS.FIX.${FIX_COPY[fix.value]}`)
    : '';
});

const ASK_OWNER_COPY = {
  turn_on: 'TURN_ON',
  go_live: 'GO_LIVE',
  request_connect: 'REQUEST_CONNECT',
  request_top_up: 'REQUEST_TOP_UP',
  request_limit_raise: 'REQUEST_LIMIT_RAISE',
};
const askOwnerLine = computed(() =>
  props.asksOwner && ASK_OWNER_COPY[fix.value]
    ? t(`WASLO.STATUS.FIX.ASK_OWNER.${ASK_OWNER_COPY[fix.value]}`)
    : ''
);

const goLiveTo = computed(() =>
  fix.value === 'go_live' && !props.goLiveHere && props.accountId !== null
    ? frontendURL(`accounts/${props.accountId}/waslo`)
    : null
);

/** `idle` → `pending` → `sent` or `error`. A new state starts it over. */
const ask = ref('idle');
const errorLine = ref('');
watch(
  () => [props.status?.state, props.status?.fix],
  () => {
    ask.value = 'idle';
    errorLine.value = '';
  }
);

const runFix = async () => {
  if (ask.value === 'pending' || ask.value === 'sent') return;
  if (fix.value === 'go_live') {
    emit('goLive');
    return;
  }
  if (props.accountId === null) return;
  ask.value = 'pending';
  errorLine.value = '';
  try {
    if (fix.value === 'turn_on') {
      const config = await wasloApi.updateConfig(props.accountId, {
        aiEnabled: true,
      });
      ask.value = 'idle';
      emit('changed', config);
      return;
    }
    if (fix.value === 'request_connect' && props.inboxId !== null) {
      await wasloApi.requestInboxConnect(props.accountId, props.inboxId);
    } else if (fix.value === 'request_connect') {
      await wasloApi.requestSetup(props.accountId, 'assistant');
    } else {
      await wasloApi.requestSetup(props.accountId, 'limit_raise');
    }
    ask.value = 'sent';
  } catch {
    ask.value = 'error';
    errorLine.value =
      fix.value === 'turn_on'
        ? t('WASLO.STATUS.FIX.SAVE_FAILED')
        : t('WASLO.STATUS.FIX.FAILED');
  }
};

const LINK_CLASS =
  'inline-flex items-center h-8 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4';
</script>

<template>
  <div
    class="flex items-start min-w-0"
    :class="size === 'large' ? 'gap-3' : 'gap-2.5'"
    :data-waslo-status-state="state ?? 'unknown'"
  >
    <span
      class="rounded-full shrink-0"
      :class="[dot, size === 'large' ? 'mt-2.5 size-2.5' : 'mt-1.5 size-2']"
      aria-hidden="true"
    />
    <div class="flex flex-col min-w-0 gap-1" aria-live="polite">
      <h2
        v-if="size === 'large'"
        class="mb-0 text-2xl font-medium leading-8 text-n-slate-12"
        data-waslo-status-headline
      >
        {{ title }}
      </h2>
      <p
        v-else
        class="mb-0 text-sm font-medium leading-5 text-n-slate-12"
        data-waslo-status-headline
      >
        {{ title }}
      </p>
      <p
        v-if="body"
        class="mb-0 text-n-slate-11"
        :class="size === 'large' ? 'text-sm' : 'text-xs leading-5'"
        data-waslo-status-detail
      >
        {{ body }}
      </p>
      <p
        v-if="resumesLine"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-status-resumes
      >
        {{ resumesLine }}
      </p>
      <slot />
      <p
        v-if="askOwnerLine"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-status-ask-owner
      >
        {{ askOwnerLine }}
      </p>
      <TopUpRequestButton
        v-else-if="fix === 'request_top_up'"
        class="pt-1"
        :account-id="accountId"
        :already-told="topUpTold"
        data-waslo-status-top-up
      />
      <div v-else-if="fixLabel" class="flex flex-wrap items-center gap-2 pt-1">
        <router-link
          v-if="goLiveTo"
          :to="goLiveTo"
          :class="LINK_CLASS"
          data-waslo-status-fix="go_live"
        >
          {{ fixLabel }}
        </router-link>
        <p
          v-else-if="ask === 'sent'"
          class="mb-0 text-sm text-n-teal-11"
          data-waslo-status-fix-sent
        >
          {{ t('WASLO.STATUS.FIX.SENT') }}
        </p>
        <Button
          v-else
          :label="ask === 'pending' ? t('WASLO.STATUS.FIX.SENDING') : fixLabel"
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="ask === 'pending'"
          :data-waslo-status-fix="fix"
          @click="runFix"
        />
        <p
          v-if="errorLine"
          class="mb-0 text-sm text-n-ruby-11"
          data-waslo-status-fix-error
        >
          {{ errorLine }}
        </p>
      </div>
    </div>
  </div>
</template>
